import { useEffect } from 'react';

// Restore the previous head on unmount so blog metadata cannot leak into
// application routes. This improves rendered metadata; it is not prerendering.
export default function usePageMetadata({ title, description, path, image, article = false, noindex = false }) {
  useEffect(() => {
    // Static pages carry the original shell's head values. Restore those once
    // before capturing cleanup values, so leaving a direct-loaded article
    // cannot restore that article's metadata on an unrelated route.
    const baseline = document.getElementById('blog-head-baseline');
    if (baseline) {
      const original = JSON.parse(baseline.textContent);
      document.title = original.title;
      original.entries.forEach(({ selector, attributes }) => {
        const element = document.head.querySelector(selector);
        if (!element) return;
        if (attributes === null) element.remove();
        else {
          [...element.attributes].forEach(attr => element.removeAttribute(attr.name));
          Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
        }
      });
      baseline.remove();
    }
    const undo = [];
    const previousTitle = document.title;
    document.title = title;
    undo.push(() => { document.title = previousTitle; });

    const setHead = (selector, tag, attributes) => {
      let element = document.head.querySelector(selector);
      const created = !element;
      if (created) {
        element = document.createElement(tag);
        document.head.appendChild(element);
      }
      const previous = Object.keys(attributes).map(key => [key, element.getAttribute(key)]);
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
      undo.push(() => {
        if (created) element.remove();
        else previous.forEach(([key, value]) => {
          if (value === null) element.removeAttribute(key);
          else element.setAttribute(key, value);
        });
      });
    };
    const meta = (key, value, property = false) => {
      const attribute = property ? 'property' : 'name';
      setHead(`meta[${attribute}="${key}"]`, 'meta', { [attribute]: key, content: value });
    };
    const url = `https://databackfill.com${path}`;
    const imageUrl = new URL(image || '/og-image.png', 'https://databackfill.com').href;
    meta('description', description);
    meta('og:title', title, true);
    meta('og:description', description, true);
    meta('og:url', url, true);
    meta('og:type', article ? 'article' : 'website', true);
    meta('og:image', imageUrl, true);
    meta('og:image:alt', title, true);
    meta('twitter:title', title);
    meta('twitter:description', description);
    meta('twitter:image', imageUrl);
    setHead('link[rel="canonical"]', 'link', { rel: 'canonical', href: url });
    if (noindex) meta('robots', 'noindex, follow');
    return () => undo.reverse().forEach(restore => restore());
  }, [title, description, path, image, article, noindex]);
}
